<!--
  O Full confere um comprovante de PIX/transferência da taxa de implantação (chamado New Shoes
  2026-0079, F3): abre o arquivo, vê valor, data e quem mandou, e aprova — o setup vira pago,
  como no "marcar como pago" de sempre — ou recusa com o motivo, que o franqueado vê.
-->
<template>
  <div class="conferencia" :data-testid="`setup-conferencia-${comprovante.id}`">
    <div class="linha">
      <i class="pi pi-receipt" aria-hidden="true" />
      <span class="texto">
        <strong>{{ mostrarFranquia ? `${comprovante.franquia_nome} · ` : '' }}{{ valor }}</strong>
        <span class="quieto">{{ t('pago em') }} {{ data(comprovante.pago_em) }} · {{ t('enviado por') }} {{ comprovante.enviado_por }}</span>
      </span>
      <app-button :label="comprovante.arquivo_nome || t('Abrir comprovante')" icon="pi pi-external-link" size="small" text
                  :data-testid="`setup-conferencia-abrir-${comprovante.id}`" @click="abrir" />
    </div>
    <div v-if="!recusando" class="botoes">
      <app-button :label="t('Aprovar: taxa paga')" icon="pi pi-check" size="small" severity="success" :loading="enviando"
                  :data-testid="`setup-conferencia-aprovar-${comprovante.id}`" @click="revisarComo('aprovar')" />
      <app-button :label="t('Recusar')" icon="pi pi-times" size="small" severity="danger" outlined :disabled="enviando"
                  :data-testid="`setup-conferencia-recusar-${comprovante.id}`" @click="recusando = true" />
    </div>
    <form v-else class="recusa" @submit.prevent="revisarComo('recusar')">
      <input-text-area v-model="motivo" :elem-id="`setup-conferencia-motivo-${comprovante.id}`" :data-testid="`setup-conferencia-motivo-${comprovante.id}`"
                       :label="t('Motivo da recusa (o franqueado vê)')" :rows="2" />
      <div class="botoes">
        <app-button type="submit" :label="t('Confirmar recusa')" size="small" severity="danger" :loading="enviando" :disabled="!motivo.trim()"
                    :data-testid="`setup-conferencia-confirmar-${comprovante.id}`" />
        <app-button type="button" :label="t('Cancelar')" size="small" text @click="recusando = false" />
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import InputTextArea from '../../inputs/InputTextArea.vue';
import { useSetupComprovantes, mensagemDoComprovante } from '../../composables/useSetupComprovantes';
import { valorDoComprovante, dataDoComprovante } from './comprovanteFormato';
import { eventBus } from '../../eventBus';
import type { SetupFee_ComprovanteRow } from '../../contracts/SetupFee_ComprovanteRow';

const props = withDefaults(defineProps<{ comprovante: SetupFee_ComprovanteRow; mostrarFranquia?: boolean }>(), { mostrarFranquia: false });
const emit = defineEmits<{ revisado: [acao: 'aprovar' | 'recusar'] }>();
const { t, n } = useI18n();
const { revisar, linkDoArquivo } = useSetupComprovantes();

const recusando = ref(false);
const motivo = ref('');
const enviando = ref(false);
const valor = computed(() => valorDoComprovante(props.comprovante, n));
const data = dataDoComprovante;

const abrir = async (): Promise<void> => {
  try {
    window.open(await linkDoArquivo(props.comprovante.id), '_blank', 'noopener');
  } catch (e) {
    eventBus.emit('globalMessage.error', t(mensagemDoComprovante(e)));
  }
};

const revisarComo = async (acao: 'aprovar' | 'recusar'): Promise<void> => {
  enviando.value = true;
  try {
    await revisar(props.comprovante.id, acao, acao === 'recusar' ? motivo.value : undefined);
    eventBus.emit('globalMessage.success', acao === 'aprovar' ? t('Taxa de implantação marcada como paga') : t('Comprovante recusado'));
    emit('revisado', acao);
  } catch (e) {
    eventBus.emit('globalMessage.error', t(mensagemDoComprovante(e)));
  } finally {
    enviando.value = false;
  }
};
</script>

<style scoped>
.conferencia { display: flex; flex-direction: column; gap: 10px; padding: 12px 14px; border-radius: 12px; background: #e0e9fb33; border: 1px solid #c7d6f5; }
.linha { display: flex; flex-wrap: wrap; align-items: center; gap: 8px 12px; }
.texto { flex: 1 1 240px; display: flex; flex-direction: column; gap: 2px; }
.quieto { color: #5b6168; font-size: 14px; }
.botoes { display: flex; flex-wrap: wrap; gap: 8px; }
.recusa { display: flex; flex-direction: column; gap: 8px; max-width: 560px; }
</style>
